import clsx from 'clsx'
import { type PropsWithChildren, useState } from 'react'
import { useSearchParams } from 'react-router'

import { FvLinkButton, Icon } from '@fv/client-components'
import {
  flattenPages,
  parseQueryString,
  stringifyQueryString,
} from '@fv/client-core'
import type { ShipmentSortType, SortDir } from '@fv/models'

import InfoBox from '@/components/InfoBox'
import { Loading } from '@/components/Loading'
import { supportMessage } from '@/constants'

import { HideForVendor } from '../auth/HasRole'
import {
  ShipmentDetailsSliderPanel,
  type ShipmentSliderState,
} from '../shipment-details-panel/ShipmentDetailsSliderPanel'
import { useConfirmedListParams } from '../shipment-list/hooks/useShipmentListParams'
import { columnsRegistry, Th } from './Cells'
import { useColumnPreferences } from './hooks'
import { ShipmentRow } from './ShipmentRow'
import type { TableProps } from './types'

export const ShipmentTable = (props: TableProps) => {
  const [, setSearchParams] = useSearchParams()
  const { query, onToggleSelectAll, selected } = props
  const [sliderState, setSliderState] = useState<ShipmentSliderState>()
  const { activeLoadId, qs } = useConfirmedListParams()
  const shipments = flattenPages(query.data?.pages)
  const isInitialLoading =
    query.isLoading || (query.isFetching && !shipments.length)
  const columnPrefs = useColumnPreferences()
  const visibleColumns = columnPrefs.filter(c => c.visible)

  const handleSortClick = (sort: ShipmentSortType, dir: SortDir) => {
    setSearchParams(
      stringifyQueryString({
        ...parseQueryString(qs),
        sortBy: sort,
        sortDirection: dir,
      }),
    )
  }
  if (isInitialLoading) {
    return <Loading />
  }
  if (query.isError) {
    return (
      <InfoBox icon="triangle">
        Unable to load shipments, {supportMessage}
      </InfoBox>
    )
  }
  if (!shipments.length) {
    return <InfoBox>No shipments available.</InfoBox>
  }
  return (
    <>
      <table className="w-full table-auto border-separate border-spacing-0">
        <thead className="shadow-lg sticky top-0 [&_th]:px-4 [&_th]:py-3 [&_th]:text-left [&_th]:border [&_tr:first-child_th]:border-fv-blue-200 [&_th]:font-normal [&_tr:first-child_th]:bg-fv-blue-50 [&_th]:whitespace-nowrap z-10  [&_tr:not(first-child)_th]:bg-white">
          <tr className="">
            <Th
              className={clsx(
                '!text-center sticky z-10 left-0 !bg-fv-blue-50 w-16 min-w-16',
              )}
            >
              <HideForVendor>
                <FvLinkButton
                  icon={selected.length ? 'dash' : 'check-double'}
                  onClick={onToggleSelectAll}
                />
              </HideForVendor>
            </Th>
            <Th className="text-left sticky left-[calc(4rem)] z-10">BOL#</Th>
            {visibleColumns.map(col => {
              const config = columnsRegistry[col.id]
              if (!config) return null
              return (
                <Th key={col.id} className="text-left">
                  {config.sortable && config.sortBy ? (
                    <SortLink
                      sort={config.sortBy}
                      onClick={handleSortClick}
                      initialSort={config.sortBy === 'status' ? 1 : -1}
                    >
                      {config.label}
                    </SortLink>
                  ) : (
                    config.label
                  )}
                </Th>
              )
            })}
            <Th className="!text-center sticky right-0">
              <Icon icon="ellipsis-h" className="text-fv-gray-dark" />
            </Th>
          </tr>
        </thead>
        <tbody className="[&_td]:px-4 [&_td]:py-3 [&_td]:border-fv-gray-200 [&_td]:border [&_td]:whitespace-nowrap">
          {shipments.map(load => (
            <ShipmentRow
              key={load.loadId}
              load={load}
              isActive={activeLoadId === load.loadId}
              setDetailTab={tab => setSliderState({ tab })}
              {...props}
            />
          ))}
        </tbody>
      </table>
      {activeLoadId && (
        <ShipmentDetailsSliderPanel
          loadId={activeLoadId}
          state={sliderState}
          onChange={setSliderState}
        />
      )}
    </>
  )
}

type SortLinkProps = {
  sort: ShipmentSortType
  initialSort?: SortDir
  onClick: (sort: ShipmentSortType, sortDir: SortDir) => void
}
const SortLink = ({
  sort,
  children,
  initialSort = -1,
  onClick,
}: PropsWithChildren<SortLinkProps>) => {
  const { sortBy, sortDirection } = useConfirmedListParams()
  return (
    <FvLinkButton
      theme="underlined"
      icon={
        sortBy === sort
          ? sortDirection === 1
            ? 'arrow-up'
            : 'arrow-down'
          : undefined
      }
      iconClass="text-fv-orange"
      onClick={() =>
        onClick(
          sort,
          sort === sortBy ? (sortDirection === 1 ? -1 : 1) : initialSort,
        )
      }
    >
      {children}
    </FvLinkButton>
  )
}
